/**
 * Cycle Count Page - Inventory Cycle Count System
 * 
 * Cycle counts are structured, intentional, and opinionated.
 * They compare expected vs actual quantities and capture variance with accountability.
 * 
 * Note: Backend uses /api/audits routes (alias approach - DB tables remain unchanged)
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { IntegerQuantityInput } from '../components/IntegerQuantityInput';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { Textarea } from '../components/ui/textarea';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  ClipboardCheck, ArrowLeft, CheckCircle2, XCircle, AlertTriangle,
  Loader2, Package, TrendingUp, TrendingDown, Minus, User, Clock,
  AlertOctagon, MousePointerClick, ChevronRight
} from 'lucide-react';
import { formatAuditTimestamp } from '../lib/timezone';

// Reason code display labels
const REASON_LABELS = {
  used_on_job: 'Used on Job',
  damaged: 'Damaged',
  lost: 'Lost',
  transfer_not_logged: 'Transfer Not Logged',
  count_error: 'Count Error',
  other: 'Other'
};

// Variance type styling
const VARIANCE_STYLES = {
  over: { color: 'text-blue-600', bg: 'bg-blue-50', icon: TrendingUp, label: 'Over' },
  short: { color: 'text-red-600', bg: 'bg-red-50', icon: TrendingDown, label: 'Short' },
  match: { color: 'text-green-600', bg: 'bg-green-50', icon: CheckCircle2, label: 'Match' }
};

export function CycleCount() {
  const { id: cycleCountId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { hasPerm } = useAuth();
  
  const [cycleCount, setCycleCount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [countingItem, setCountingItem] = useState(null);
  const [countValue, setCountValue] = useState('');
  const [reasonCode, setReasonCode] = useState('');
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);
  const [completing, setCompleting] = useState(false);
  const [abandonConfirm, setAbandonConfirm] = useState(false);
  const [abandoning, setAbandoning] = useState(false);
  const [filter, setFilter] = useState('all'); // all, uncounted, variance
  const [applyAdjustmentsConfirm, setApplyAdjustmentsConfirm] = useState(false);
  const [applyingAdjustments, setApplyingAdjustments] = useState(false);

  // Load cycle count data (backend uses /audits route)
  const loadCycleCount = useCallback(async () => {
    if (!cycleCountId) return;
    try {
      const response = await api.get(`/audits/${cycleCountId}`);
      setCycleCount(response.data);
    } catch (err) {
      toast.error('Failed to load cycle count');
      navigate('/warehouse');
    } finally {
      setLoading(false);
    }
  }, [cycleCountId, navigate]);

  useEffect(() => {
    loadCycleCount();
  }, [loadCycleCount]);

  // Handle count submission
  const handleCountSubmit = async () => {
    if (!countingItem) return;
    
    const actualQty = parseInt(countValue, 10);
    if (isNaN(actualQty) || actualQty < 0) {
      toast.error('Please enter a valid quantity');
      return;
    }

    const variance = actualQty - countingItem.expected_qty;
    
    // Validate reason code requirement
    if (variance !== 0 && !reasonCode) {
      toast.error('Reason code is required when quantity differs from expected');
      return;
    }
    
    // Validate note for "other" reason
    if (reasonCode === 'other' && !note.trim()) {
      toast.error('Note is required when reason is "Other"');
      return;
    }

    setSaving(true);
    try {
      await api.put(`/audits/${cycleCountId}/items/${countingItem.item_id}`, {
        actual_qty: actualQty,
        reason_code: variance !== 0 ? reasonCode : null,
        note: reasonCode === 'other' ? note.trim() : null
      });
      
      toast.success(`Count recorded for ${countingItem.item_name}`);
      setCountingItem(null);
      setCountValue('');
      setReasonCode('');
      setNote('');
      loadCycleCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to record count');
    } finally {
      setSaving(false);
    }
  };

  // Handle complete cycle count
  const handleComplete = async () => {
    setCompleting(true);
    try {
      await api.post(`/audits/${cycleCountId}/complete`);
      toast.success('Cycle count completed successfully');
      loadCycleCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to complete cycle count');
    } finally {
      setCompleting(false);
    }
  };

  // Handle abandon cycle count
  const handleAbandon = async () => {
    setAbandoning(true);
    try {
      await api.post(`/audits/${cycleCountId}/abandon`);
      toast.success('Cycle count abandoned');
      navigate(searchParams.get('returnTo') || '/warehouse');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to abandon cycle count');
    } finally {
      setAbandoning(false);
      setAbandonConfirm(false);
    }
  };

  // Handle apply adjustments
  const handleApplyAdjustments = async () => {
    setApplyingAdjustments(true);
    try {
      const response = await api.post(`/audits/${cycleCountId}/apply-adjustments`);
      const { items_adjusted, total_qty_delta } = response.data;
      toast.success(`Adjustments applied: ${items_adjusted} items updated (total delta: ${total_qty_delta >= 0 ? '+' : ''}${total_qty_delta})`);
      setApplyAdjustmentsConfirm(false);
      loadCycleCount();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to apply adjustments');
    } finally {
      setApplyingAdjustments(false);
    }
  };

  // Open count dialog
  const openCountDialog = (item) => {
    setCountingItem(item);
    setCountValue(item.actual_qty?.toString() || '');
    setReasonCode(item.reason_code || '');
    setNote(item.note || '');
  };

  // Filter items
  const getFilteredItems = () => {
    if (!cycleCount?.items) return [];
    
    switch (filter) {
      case 'uncounted':
        return cycleCount.items.filter(i => i.actual_qty === null);
      case 'variance':
        return cycleCount.items.filter(i => i.variance_type && i.variance_type !== 'match');
      default:
        return cycleCount.items;
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-primary" />
        </div>
      </Layout>
    );
  }

  if (!cycleCount) {
    return (
      <Layout>
        <div className="text-center text-muted-foreground py-8">
          Cycle count not found
        </div>
      </Layout>
    );
  }

  const filteredItems = getFilteredItems();
  const progress = cycleCount.total_items > 0 
    ? Math.round((cycleCount.counted_items / cycleCount.total_items) * 100) 
    : 0;
  const canComplete = cycleCount.status === 'in_progress' && cycleCount.counted_items === cycleCount.total_items;
  const isReadOnly = cycleCount.status !== 'in_progress';
  const hasVariances = cycleCount.items_over + cycleCount.items_short > 0;
  const canApplyAdjustments = cycleCount.status === 'completed' && hasVariances && !cycleCount.adjustments_applied_at;

  // Determine current step for instruction banner
  const getCurrentStep = () => {
    if (cycleCount.status !== 'in_progress') return null;
    if (cycleCount.counted_items === 0) return 1; // Step 1: Count Items
    if (cycleCount.counted_items < cycleCount.total_items) return 1; // Still counting
    if (cycleCount.items_over + cycleCount.items_short > 0) return 2; // Step 2: Review Variances
    return 3; // Step 3: Complete Cycle Count
  };
  
  const currentStep = getCurrentStep();

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon"
              className="h-10 w-10"
              onClick={() => navigate(searchParams.get('returnTo') || '/warehouse')}
              data-testid="cycle-count-back-btn"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
              <ClipboardCheck className="w-6 h-6 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold truncate" data-testid="cycle-count-title">
                  {cycleCount.audit_type === 'full' ? 'Full' : 'Category'} Cycle Count
                </h1>
                <Badge variant={
                  cycleCount.status === 'completed' ? 'default' :
                  cycleCount.status === 'abandoned' ? 'destructive' :
                  'secondary'
                } data-testid="cycle-count-status">
                  {cycleCount.status === 'in_progress' ? 'In Progress' : 
                   cycleCount.status.charAt(0).toUpperCase() + cycleCount.status.slice(1)}
                </Badge>
              </div>
              <p className="text-sm text-muted-foreground">
                {cycleCount.target_name} • Started by {cycleCount.started_by_name}
              </p>
            </div>
          </div>
          
          {/* Desktop Action Buttons */}
          <div className="hidden md:flex items-center gap-2">
            {cycleCount.status === 'in_progress' && (
              <>
                <Button 
                  variant="outline" 
                  onClick={() => setAbandonConfirm(true)}
                  data-testid="abandon-cycle-count-btn"
                >
                  Abandon
                </Button>
                <Button 
                  disabled={!canComplete || completing}
                  onClick={handleComplete}
                  data-testid="complete-cycle-count-btn"
                >
                  {completing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 mr-2" />
                  )}
                  Complete Cycle Count
                </Button>
              </>
            )}
          </div>
        </div>

        {/* Instruction Banner - Dynamic Steps */}
        {cycleCount.status === 'in_progress' && (
          <Card className="border-primary/30 bg-primary/5">
            <CardContent className="p-4">
              <div className="flex items-center gap-4">
                {/* Step indicators */}
                <div className="hidden sm:flex items-center gap-2">
                  {[1, 2, 3].map((step) => (
                    <div key={step} className="flex items-center">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold ${
                        step === currentStep 
                          ? 'bg-primary text-primary-foreground' 
                          : step < currentStep 
                            ? 'bg-green-500 text-white'
                            : 'bg-muted text-muted-foreground'
                      }`}>
                        {step < currentStep ? <CheckCircle2 className="w-4 h-4" /> : step}
                      </div>
                      {step < 3 && (
                        <ChevronRight className={`w-4 h-4 mx-1 ${step < currentStep ? 'text-green-500' : 'text-muted-foreground'}`} />
                      )}
                    </div>
                  ))}
                </div>
                
                {/* Step instruction text */}
                <div className="flex-1">
                  <p className="font-semibold text-primary">
                    {currentStep === 1 && `Step 1 of 3: Count Items`}
                    {currentStep === 2 && `Step 2 of 3: Review Variances`}
                    {currentStep === 3 && `Step 3 of 3: Complete Cycle Count`}
                  </p>
                  <p className="text-sm text-muted-foreground">
                    {currentStep === 1 && `Click each item below to enter the physical count. (${cycleCount.counted_items}/${cycleCount.total_items} done)`}
                    {currentStep === 2 && `${cycleCount.items_over + cycleCount.items_short} item(s) have variances. Review reason codes, then complete.`}
                    {currentStep === 3 && `All items counted and match expected. Click "Complete Cycle Count" to finish.`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Progress Card */}
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium">Progress</span>
              <span className="text-sm text-muted-foreground">
                {cycleCount.counted_items} / {cycleCount.total_items} items
              </span>
            </div>
            <Progress value={progress} className="h-2" />
            
            {/* Summary Stats */}
            <div className="grid grid-cols-3 gap-4 mt-4">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-green-50 flex items-center justify-center">
                  <CheckCircle2 className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold">{cycleCount.items_match}</p>
                  <p className="text-xs text-muted-foreground">Match</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold">{cycleCount.items_short}</p>
                  <p className="text-xs text-muted-foreground">Short</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                  <TrendingUp className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <p className="text-lg font-semibold">{cycleCount.items_over}</p>
                  <p className="text-xs text-muted-foreground">Over</p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Category badges (for category cycle counts) */}
        {cycleCount.category_names && cycleCount.category_names.length > 0 && (
          <div className="flex flex-wrap gap-1">
            <span className="text-sm text-muted-foreground mr-2">Categories:</span>
            {cycleCount.category_names.map((name, i) => (
              <Badge key={i} variant="outline">{name}</Badge>
            ))}
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex gap-2">
          <Button 
            variant={filter === 'all' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('all')}
            data-testid="filter-all-btn"
          >
            All ({cycleCount.total_items})
          </Button>
          <Button 
            variant={filter === 'uncounted' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('uncounted')}
            data-testid="filter-uncounted-btn"
          >
            Uncounted ({cycleCount.total_items - cycleCount.counted_items})
          </Button>
          <Button 
            variant={filter === 'variance' ? 'default' : 'outline'} 
            size="sm"
            onClick={() => setFilter('variance')}
            data-testid="filter-variance-btn"
          >
            Variance ({cycleCount.items_over + cycleCount.items_short})
          </Button>
        </div>

        {/* Items list */}
        <div className="space-y-2" data-testid="cycle-count-items-list">
          {filteredItems.length === 0 ? (
            <Card>
              <CardContent className="p-8 text-center text-muted-foreground">
                <Package className="w-12 h-12 mx-auto mb-2 opacity-50" />
                <p>No items to display</p>
              </CardContent>
            </Card>
          ) : (
            filteredItems.map(item => {
              const isCounted = item.actual_qty !== null;
              const varianceStyle = item.variance_type ? VARIANCE_STYLES[item.variance_type] : null;
              
              return (
                <Card 
                  key={item.id} 
                  className={`transition-colors ${
                    isReadOnly ? '' : 'cursor-pointer hover:border-primary hover:shadow-md'
                  } ${isCounted ? 'border-l-4' : 'border-l-4 border-l-orange-400'} ${
                    varianceStyle ? `border-l-${varianceStyle.color.split('-')[1]}-500` : ''
                  }`}
                  onClick={() => !isReadOnly && openCountDialog(item)}
                  data-testid={`cycle-count-item-${item.item_id}`}
                >
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <p className="font-medium truncate">{item.item_name}</p>
                          {item.item_sku && (
                            <span className="text-xs text-muted-foreground">
                              {item.item_sku}
                            </span>
                          )}
                        </div>
                        {item.category_names && item.category_names.length > 0 && (
                          <p className="text-xs text-muted-foreground mt-1">
                            {item.category_names.join(', ')}
                          </p>
                        )}
                        {/* Count info */}
                        {isCounted && (
                          <div className="flex items-center gap-4 mt-2 text-sm">
                            <span className="text-muted-foreground">
                              Expected: <span className="font-medium text-foreground">{item.expected_qty}</span>
                            </span>
                            <span className="text-muted-foreground">
                              Actual: <span className="font-medium text-foreground">{item.actual_qty}</span>
                            </span>
                            {item.variance !== 0 && (
                              <span className={varianceStyle?.color}>
                                {item.variance > 0 ? '+' : ''}{item.variance}
                              </span>
                            )}
                          </div>
                        )}
                        {/* Reason & Note */}
                        {item.reason_code && (
                          <div className="mt-2 text-sm">
                            <Badge variant="outline" className="text-xs">
                              {REASON_LABELS[item.reason_code] || item.reason_code}
                            </Badge>
                            {item.note && (
                              <p className="text-muted-foreground mt-1 text-xs italic">
                                &ldquo;{item.note}&rdquo;
                              </p>
                            )}
                          </div>
                        )}
                        {/* Counted by - with timezone */}
                        {item.counted_by_name && (
                          <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                            <User className="w-3 h-3" />
                            {item.counted_by_name}
                            <Clock className="w-3 h-3 ml-2" />
                            {formatAuditTimestamp(item.counted_at)}
                          </div>
                        )}
                      </div>
                      
                      {/* Right side - expected qty or variance badge or Count button */}
                      <div className="text-right flex flex-col items-end gap-2">
                        {!isCounted ? (
                          <>
                            <div>
                              <p className="text-2xl font-bold">{item.expected_qty}</p>
                              <p className="text-xs text-muted-foreground">expected</p>
                            </div>
                            {/* Explicit click affordance for uncounted items */}
                            {!isReadOnly && (
                              <div className="flex items-center gap-1 text-xs text-primary font-medium">
                                <MousePointerClick className="w-3.5 h-3.5" />
                                <span>Click to count</span>
                              </div>
                            )}
                          </>
                        ) : varianceStyle ? (
                          <div className={`px-3 py-1 rounded-full ${varianceStyle.bg}`}>
                            <varianceStyle.icon className={`w-5 h-5 ${varianceStyle.color}`} />
                          </div>
                        ) : null}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              );
            })
          )}
        </div>

        {/* Mobile action buttons */}
        {cycleCount.status === 'in_progress' && (
          <div className="flex gap-2 md:hidden pt-4 border-t">
            <Button 
              variant="outline" 
              className="flex-1"
              onClick={() => setAbandonConfirm(true)}
            >
              Abandon
            </Button>
            <Button 
              className="flex-1"
              disabled={!canComplete || completing}
              onClick={handleComplete}
            >
              {completing ? (
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <CheckCircle2 className="w-4 h-4 mr-2" />
              )}
              Complete
            </Button>
          </div>
        )}

        {/* Completed/Abandoned info */}
        {isReadOnly && (
          <Card className={cycleCount.status === 'completed' ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 flex-wrap">
                {cycleCount.status === 'completed' ? (
                  <CheckCircle2 className="w-5 h-5 text-green-600" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-600" />
                )}
                <span className="font-medium">
                  {cycleCount.status === 'completed' ? 'Cycle Count Completed' : 'Cycle Count Abandoned'}
                </span>
                <span className="text-sm text-muted-foreground ml-auto">
                  {formatAuditTimestamp(cycleCount.completed_at || cycleCount.abandoned_at)}
                </span>
              </div>
              
              {/* Adjustments Section - only for completed cycle counts with variances */}
              {cycleCount.status === 'completed' && hasVariances && (
                <div className="mt-4 pt-4 border-t border-green-200">
                  {cycleCount.adjustments_applied_at ? (
                    <div className="flex items-center gap-2 text-sm text-green-700">
                      <CheckCircle2 className="w-4 h-4" />
                      <span>
                        Adjustments applied {cycleCount.adjustments_applied_by_name ? `by ${cycleCount.adjustments_applied_by_name}` : ''} 
                        {' • '}
                        {formatAuditTimestamp(cycleCount.adjustments_applied_at)}
                      </span>
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                      <div className="text-sm">
                        <p className="font-medium text-foreground">
                          {cycleCount.items_over + cycleCount.items_short} item(s) have variances
                        </p>
                        <p className="text-muted-foreground">
                          Apply adjustments to update inventory quantities to match actual counts
                        </p>
                      </div>
                      <Button 
                        onClick={() => setApplyAdjustmentsConfirm(true)}
                        className="shrink-0"
                        data-testid="apply-adjustments-btn"
                      >
                        <TrendingUp className="w-4 h-4 mr-2" />
                        Apply Adjustments
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Count Dialog */}
      <Dialog open={!!countingItem} onOpenChange={() => setCountingItem(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Count</DialogTitle>
            <DialogDescription>
              {countingItem?.item_name}
              {countingItem?.item_sku && ` (${countingItem.item_sku})`}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Expected quantity display */}
            <div className="p-4 bg-muted rounded-lg text-center">
              <p className="text-sm text-muted-foreground">Expected Quantity</p>
              <p className="text-3xl font-bold">{countingItem?.expected_qty}</p>
            </div>
            
            {/* Actual count input */}
            <div className="space-y-2">
              <Label>Actual Count</Label>
              <IntegerQuantityInput
                min="0"
                value={countValue}
                onChange={(val) => setCountValue(val)}
                placeholder="Enter actual quantity"
                autoFocus
                data-testid="actual-count-input"
              />
            </div>
            
            {/* Variance indicator */}
            {countValue !== '' && !isNaN(parseInt(countValue)) && (
              <div className="p-3 rounded-lg bg-muted">
                {(() => {
                  const variance = parseInt(countValue) - (countingItem?.expected_qty || 0);
                  if (variance === 0) {
                    return (
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle2 className="w-5 h-5" />
                        <span className="font-medium">Match</span>
                      </div>
                    );
                  } else if (variance > 0) {
                    return (
                      <div className="flex items-center gap-2 text-blue-600">
                        <TrendingUp className="w-5 h-5" />
                        <span className="font-medium">Over by {variance}</span>
                      </div>
                    );
                  } else {
                    return (
                      <div className="flex items-center gap-2 text-red-600">
                        <TrendingDown className="w-5 h-5" />
                        <span className="font-medium">Short by {Math.abs(variance)}</span>
                      </div>
                    );
                  }
                })()}
              </div>
            )}
            
            {/* Reason code (only shown if variance) */}
            {countValue !== '' && parseInt(countValue) !== countingItem?.expected_qty && (
              <>
                <div className="space-y-2">
                  <Label>Reason Code <span className="text-destructive">*</span></Label>
                  <Select value={reasonCode} onValueChange={setReasonCode}>
                    <SelectTrigger data-testid="reason-code-select">
                      <SelectValue placeholder="Select reason..." />
                    </SelectTrigger>
                    <SelectContent>
                      {Object.entries(REASON_LABELS).map(([value, label]) => (
                        <SelectItem key={value} value={value}>{label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                {/* Note (required for "other") */}
                {reasonCode === 'other' && (
                  <div className="space-y-2">
                    <Label>Note <span className="text-destructive">*</span></Label>
                    <Textarea
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder="Explain the variance..."
                      rows={3}
                      data-testid="variance-note-input"
                    />
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setCountingItem(null)}>
              Cancel
            </Button>
            <Button onClick={handleCountSubmit} disabled={saving} data-testid="save-count-btn">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Save Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Abandon Confirmation */}
      <Dialog open={abandonConfirm} onOpenChange={setAbandonConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertOctagon className="w-5 h-5" />
              Abandon Cycle Count?
            </DialogTitle>
            <DialogDescription>
              This cycle count will be marked as abandoned and cannot be resumed.
              All recorded counts will be preserved for record.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAbandonConfirm(false)}>
              Cancel
            </Button>
            <Button variant="destructive" onClick={handleAbandon} disabled={abandoning} data-testid="confirm-abandon-btn">
              {abandoning && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Abandon Cycle Count
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Apply Adjustments Confirmation */}
      <Dialog open={applyAdjustmentsConfirm} onOpenChange={setApplyAdjustmentsConfirm}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Apply Inventory Adjustments?
            </DialogTitle>
            <DialogDescription>
              This will update inventory quantities to match the actual counts recorded in this cycle count.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 py-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Items with variances:</span>
              <span className="font-medium">{cycleCount?.items_over + cycleCount?.items_short}</span>
            </div>
            {cycleCount?.items_short > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-red-600">
                  <TrendingDown className="w-4 h-4" /> Short items:
                </span>
                <span className="font-medium text-red-600">{cycleCount.items_short}</span>
              </div>
            )}
            {cycleCount?.items_over > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="flex items-center gap-1 text-blue-600">
                  <TrendingUp className="w-4 h-4" /> Over items:
                </span>
                <span className="font-medium text-blue-600">{cycleCount.items_over}</span>
              </div>
            )}
            
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                <div>
                  <p className="font-medium">This action cannot be undone.</p>
                  <p className="text-amber-700 mt-1">
                    Inventory quantities will be updated immediately. An audit trail will be created for each adjustment.
                  </p>
                </div>
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setApplyAdjustmentsConfirm(false)}>
              Cancel
            </Button>
            <Button onClick={handleApplyAdjustments} disabled={applyingAdjustments} data-testid="confirm-apply-adjustments-btn">
              {applyingAdjustments && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Apply Adjustments
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}

export default CycleCount;
